import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Edit2 } from 'lucide-react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { useChatStore } from '../../store/chatStore';

interface RenameThreadForm {
  title: string;
}

const schema = yup.object().shape({
  title: yup
    .string()
    .required('Thread title is required')
    .max(200, 'Title must be less than 200 characters'),
}) as yup.ObjectSchema<RenameThreadForm>;

interface RenameThreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  threadId?: string;
  currentTitle?: string;
}

const RenameThreadModal: React.FC<RenameThreadModalProps> = ({
  isOpen,
  onClose,
  threadId,
  currentTitle,
}) => {
  const { updateThread } = useChatStore();
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RenameThreadForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      title: currentTitle || '',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      reset({
        title: currentTitle || '',
      });
    }
  }, [isOpen, currentTitle, reset]);

  const onSubmit = async (data: RenameThreadForm) => {
    if (!threadId) return;
    
    setIsLoading(true);
    try {
      await updateThread(threadId, { title: data.title });
      onClose();
    } catch (error) {
      // Error handled by toast in store
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rename Thread" maxWidth="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          {...register('title')}
          label="Thread Title"
          placeholder="Enter new thread title"
          error={errors.title?.message}
          autoFocus
        />

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            icon={Edit2}
            loading={isLoading}
            disabled={isLoading}
          >
            Rename Thread
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RenameThreadModal;
